import api from './api';

export type ReportType = 'Review' | 'Comment' | 'User';

export type ReportReason =
  | 'Spam'
  | 'Harassment'
  | 'HateSpeech'
  | 'Inappropriate'
  | 'FalseInformation'
  | 'Other';

export interface ReportRequest {
  type: ReportType;
  targetId: string;
  reason: ReportReason;
  description?: string;
}

export interface ReportResponse {
  id: string;
  message: string;
}

export interface BlockedUser {
  id: string;
  username: string;
  profileImageUrl?: string;
  blockedAt: string;
}

// Convert reason string to enum value expected by API
function getReasonValue(reason: ReportReason): number {
  switch (reason) {
    case 'Spam':
      return 0;
    case 'Harassment':
      return 1;
    case 'HateSpeech':
      return 2;
    case 'Inappropriate':
      return 3;
    case 'FalseInformation':
      return 4;
    case 'Other':
      return 5;
    default:
      return 5;
  }
}

// Convert type string to enum value expected by API
function getTypeValue(type: ReportType): number {
  switch (type) {
    case 'Review':
      return 0;
    case 'Comment':
      return 1;
    case 'User':
      return 2;
    default:
      return 0;
  }
}

export const moderationService = {
  async report(request: ReportRequest): Promise<ReportResponse> {
    const response = await api.post<ReportResponse>('/moderation/report', {
      type: getTypeValue(request.type),
      targetId: request.targetId,
      reason: getReasonValue(request.reason),
      description: request.description,
    });
    return response.data;
  },

  async blockUser(userId: string): Promise<void> {
    await api.post('/moderation/block', { userId });
  },

  async unblockUser(userId: string): Promise<void> {
    await api.delete(`/moderation/block/${userId}`);
  },

  async getBlockedUsers(): Promise<string[]> {
    const response = await api.get<string[]>('/moderation/blocked');
    return response.data;
  },

  async getBlockedUsersWithDetails(): Promise<BlockedUser[]> {
    const response = await api.get<BlockedUser[]>('/moderation/blocked/details');
    return response.data;
  },

  async isUserBlocked(userId: string): Promise<boolean> {
    const response = await api.get<{ isBlocked: boolean }>(
      `/moderation/blocked/${userId}`
    );
    return response.data.isBlocked;
  },
};
